/**
 * Ryan O'Dowd
 * 2022-03-15
 * © Copyright 2022 Oakwood Software Consulting, Inc. All Rights Reserved.
 */
import {
  apiSaveLesson,
  fetchUser,
  getRequestHeaders,
  setApiSyncStagingMetadata,
} from '@utils/store/actions';
import {
  add as dateFnsAdd,
  format as dateFnsFormat,
} from 'date-fns';
import CPassage from '@classes/Passage';
import commonUtils from './common';
import fb from '@utils/firebase';
import {
  fetch as fetchNetworkInfo,
} from '@react-native-community/netinfo';
import globals from '@utils/globals';
import {
  i18nUnitsTime,
} from './i18n';
import {
  store,
} from '@utils/store';
import uploadPhoto from './uploadPhoto';
import {
  v4 as uuidv4,
} from 'uuid';

function _getLocaleId() {
  return store.getState().userSettings?.locale_id || 1;
}

function capitalizeFirstLetter(s) {
  // https://stackoverflow.com/questions/1026069/how-do-i-make-the-first-letter-of-a-string-uppercase-in-javascript
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function humanReadableScore(score) { // @TODO: are these suffixes different for different languages?
  let suffix = '';
  if (score > 1000000000) {
    score /= 1000000000;
    suffix = 'B';
  } else if (score > 1000000) {
    score /= 1000000;
    suffix = 'M';
  } else if (score > 1000) {
    score /= 1000;
    suffix = 'K';
  }

  score = Math.floor(score * 10) / 10;
  const scoreRet = `${score}${suffix}`;

  const localeId = _getLocaleId();
  if (localeId !== 1) {
    return scoreRet.replace('.', ','); // @TODO: is this true of all none en langs?
  }

  return scoreRet;
}

function humanReadableTime(ms) {
  let denomination = ms;
  let units = 'millisecond';
  if (denomination / 1000 >= 1) {
    denomination /= 1000;
    units = 'second';
    if (denomination / 60 >= 1) {
      denomination /= 60;
      units = 'minute';
      if (denomination / 60 >= 1) {
        denomination /= 60;
        units = 'hour';
      }
    }
  }

  const localeId = _getLocaleId();
  if (localeId !== 1) {
    units = i18nUnitsTime[localeId][units];
  }

  denomination = Math.floor(denomination);
  const retVal = `${denomination.toLocaleString()} ${units}${denomination === 1 ? '' : 's'}`; // @TODO: nice to do toLocaleString here, but watch out for the replace below

  if (localeId !== 1) {
    return retVal.replace('.', ','); // @TODO: is this true of all non-en langs?
  }

  return retVal;
}

function humanReadableTimeExact(ms) {
  const hours = Math.floor(ms / (60 * 60 * 1000));
  const minutes = Math.floor((ms - (hours * 60 * 60 * 1000)) / (60 * 1000));
  const seconds = Math.floor((ms - (hours * 60 * 60 * 1000) - (minutes * 60 * 1000)) / 1000);

  if (hours > 0) {
    return `${hours}h ${minutes}m ${seconds}s`;
  } else if (minutes > 0) {
    return `${minutes}m ${seconds}s`;
  }

  return `${seconds}s`;
}

function humanReadableTimeDashboard(ms) {
  let denomination = ms;
  let units = 'millisecond';
  if (denomination / 1000 >= 1) {
    denomination /= 1000;
    units = 'second';
    if (denomination / 60 >= 1) {
      denomination /= 60;
      units = 'minute';
      /* @TODO: wait until this is in the 100s/1000s of hours (and then just combine with humanReadableTime)
      if (denomination / 60 >= 1) {
        denomination /= 60;
        units = 'hour';
      }
      */
    }
  }

  denomination = Math.floor(denomination);
  return `${denomination} ${units}${denomination === 1 ? '' : 's'}`;
}

function humanReadableNumber(numVerses) { // @TODO: are these suffixes different for different languages?
  let denomination = numVerses; // @TODO: why is this called denomination?
  let units = '';
  if (denomination / 1000 >= 1) {
    denomination /= 1000;
    units = 'k';
    if (denomination / 1000 >= 1) {
      denomination /= 1000;
      units = 'm';
      if (denomination / 1000 >= 1) {
        denomination /= 1000;
        units = 'b';
      }
    }
  }

  if (['b'].includes(units)) {
    // 2 decimals places is better for larger numbers
    denomination = Math.floor(denomination * 100) / 100;
  } else {
    denomination = Math.floor(denomination * 10) / 10;
  }
  const retVal = `${denomination.toLocaleString()}${units}`; // @TODO: nice to do toLocaleString here, but watch out for the replace below

  const localeId = _getLocaleId();
  if (localeId !== 1) {
    return retVal.replace('.', ','); // @TODO: is this true of all none en langs?
  }

  return retVal;
}

function humanReadableBytes(bytes) { // @TODO: are these suffixes different for different languages?
  let amount = bytes;
  let units = 'bytes'; // @TODO: i18n
  if (amount / 1000 >= 1) {
    amount /= 1000;
    units = 'kB';
    if (amount / 1000 >= 1) {
      amount /= 1000;
      units = 'MB';
      if (amount / 1000 >= 1) {
        amount /= 1000;
        units = 'GB';
      }
    }
  }

  amount = Math.floor(amount * 100) / 100;
  return `${amount} ${units}`;
}

function prettyCurrency(pCents) { // @TODO: can maybe replace with .toLocaleString or something similar
  let [dollars, cents] = `${pCents / 100}`.split('.');
  if (dollars.length > 4) {
    dollars = dollars.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }
  cents = cents ? `.${(cents || '0').padEnd(2, '0')}` : '';

  return `$${dollars || ''}${cents}`;
}

async function createNewPassage(translationId, bookId, chapterNumber, startVerseNumber, endVerseNumber) { // @TODO: should return CPassage
  const response = await fetch(`${globals.apiUrl}/passages`, {
    method: 'POST',
    headers: {
      ...await getRequestHeaders(true), // @TODO: is this how we do auth for fetch api? // @TODO: dangerous to ignore auth here because anyone could clobber this endpoint, but it's needed right now for sharing passages when people aren't logged in
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      translation_id: translationId,
      book_id: bookId,
      chapter_number: chapterNumber,
      start_verse_number: startVerseNumber,
      end_verse_number: endVerseNumber,
    }),
  }); // @TODO: add auth headers
  if (response.status !== 200) {
    return null; // @TODO: throw error to user
  }
  const responseJson = await response.json();

  return await responseJson.passage;
}

function removeVerseFormatting(verseText) {
  return verseText?.trim().replace(/([ ]+)?\n([ ]+)?/g, ' ') || ''; // @TODO: don't replace with space if dash or elipsis
}

function getGoalProgress(goalId) { // @TODO: in theory, it seems like this shouldn't be performant enough for the front end to do, but in practice, i think it's fine // @TODO: should this be a custom hook? memoized?
  const globalStore = store.getState(); // @TODO: replace all uses of store.getState with useTypedSelector
  const goal = globalStore.goals[goalId];
  const userGoalProgress = globalStore.goalProgress[goalId]?.[globalStore.oakUser.id] || {};

  let numCompletedModules = 0;
  let numInProgressModules = 0;
  let numCompletedAssignments = 0;
  let numInProgressAssignments = 0;
  let numAssignments = 0;
  let numVersesInCompletedAssignments = 0;
  let numVersesInInProgressAssignments = 0;
  let numPeanutsEarned = userGoalProgress.assignmentless?.total_peanuts_earned || 0; // some goals have peanuts not attached to specific assignments (especially verse memories)
  goal?.modules.forEach((m) => {
    let moduleIsCompleted = true;
    let moduleIsInProgress = false;
    m.assignments.forEach((a) => {
      numAssignments++;

      const assignmentProgress = userGoalProgress[a.id] || {};
      const assignmentIsCompleted = assignmentProgress.nlc >= assignmentProgress.nlr;
      const assignmentIsInProgress = !assignmentIsCompleted && assignmentProgress.nlc;

      numPeanutsEarned += assignmentProgress.p || 0;

      if (assignmentIsCompleted) {
        numCompletedAssignments++;
        moduleIsInProgress = true;
      } else {
        moduleIsCompleted = false;
      }

      if (assignmentIsInProgress) {
        numInProgressAssignments++;
        moduleIsCompleted = false;
        moduleIsInProgress = true;
      }

      if (assignmentIsCompleted && a.assignment_object.passage) {
        numVersesInCompletedAssignments += (a.assignment_object.passage.end_verse_number - a.assignment_object.passage.start_verse_number + 1);
      } else if (assignmentIsInProgress && a.assignment_object.passage) {
        numVersesInInProgressAssignments += (a.assignment_object.passage.end_verse_number - a.assignment_object.passage.start_verse_number + 1);
      }
    });

    if (moduleIsCompleted) {
      numCompletedModules++;
    } else if (moduleIsInProgress) {
      numInProgressModules++;
    }
  });

  return {
    numCompletedModules,
    numInProgressModules,
    numCompletedAssignments,
    numInProgressAssignments,
    numAssignments,
    numVersesInCompletedAssignments,
    numVersesInInProgressAssignments,
    numPeanutsEarned,
  };

}

function isNewAppVersionWithReleaseNotesAvailable() { // @TODO: move to commonUtils
  return store.getState().whatsNewNotes.some((n) => {
    const releaseDatePlusOneDay = dateFnsAdd(new Date(n.release_date), {days: 1});
    return (
      (!commonUtils.isSecondOlderOrSameAsFirst(globals.appVersionNumber, n.app_version)) && // note for a newer app version // @TODO: this is always true (difference in logic between showing NewVersion (not on most recent version) and showing badge on WhatsNew (on current version but have unread notes))
      (new Date(releaseDatePlusOneDay).getTime() < new Date().getTime()) // give up to 24 hours or grace period for update to propogate
    );
  });
}

function prettyAvailablityDateRange(startDate, endDate) {
  const formattedStart = dateFnsFormat(new Date(startDate ?? new Date()), 'MMM d, yyyy');
  const formattedEnd = dateFnsFormat(new Date(endDate ?? new Date()), 'MMM d, yyyy');

  if (!startDate && !endDate) {
    return 'Available any time';
  } else if (startDate && !endDate) {
    return `Available from ${formattedStart}`;
  } else if (!startDate && endDate) {
    return `Due by ${formattedEnd}`;
  }

  return `Available ${formattedStart} - ${formattedEnd}`;
}

function retryFailedCompletedLessons(userId) { // @TODO: remove userId param in or after v5.2.0 // @TODO: should this be in actions instead of utilities?
  fetchNetworkInfo().then((state) => {
    if (!state.isConnected) {
      // no use retrying completed lessons if there's no network
      return;
    }

    const numTotalCompletedLessons = Object.values(store.getState().completedLessons).length;
    Object.values(store.getState().completedLessons).forEach((lesson: any, index: number) => {
      // @TODO: there's a better way to batch save these than calling n netwrok calls for n saved lessons...the n hits to redux are also a little much
      const thisIsTheLastCompletedLesson = numTotalCompletedLessons === index + 1;
      store.dispatch(apiSaveLesson(
        lesson.userId || userId,
        lesson.uuid,
        lesson.goalId,
        lesson.passageId,
        lesson.assignmentId,
        lesson.difficulty,
        lesson.millisecondsSpent,
        lesson.peanutsEarned,
        lesson.numberOfQuestions,
        lesson.numberOfVerses,
        lesson.minigameIds,
        lesson.repairStreakDate,
        lesson.adHocPassage,
        lesson.testPassedVerseNums,
        lesson.dateCompleted,
        lesson.passageOfTheDayId,
        lesson.streakDateTz,
        lesson.error,
        lesson.numberOfSkippedQuestions,
        !thisIsTheLastCompletedLesson,
        thisIsTheLastCompletedLesson,
        lesson.appVersion,
      ));
    });
  });
}

function saveLesson({
  userId,
  passageId,
  millisecondsSpent,
  peanutsEarned,
  difficulty,
  numberOfQuestions,
  numberOfVersesReviewed,
  minigameIds,
  goalId,
  testPassedVerseNums,
  assignmentId,
  adHocPassage,
  streakRepairDate,
  passageOfTheDayId,
  numberOfSkippedQuestions,
}) { // @TODO: throw error if any of these are missing/named incorectly
  store.dispatch(apiSaveLesson(
    userId,
    uuidv4(),
    goalId,
    passageId, // @TODO: would be nice to save an array of passages in the database, but this will require schema changes
    assignmentId,
    difficulty,
    millisecondsSpent,
    peanutsEarned,
    numberOfQuestions,
    numberOfVersesReviewed,
    minigameIds,
    streakRepairDate,
    adHocPassage,
    testPassedVerseNums,
    Date.now(),
    passageOfTheDayId,
    dateFnsFormat(new Date(), 'yyyy-MM-dd'),
    undefined, // no error yet
    numberOfSkippedQuestions,
    false,
    false,
    globals.appVersionNumber,
  ));

  // try to save previously completed lessons that failed to upload as well
  retryFailedCompletedLessons(userId);
}

const isVerseWrinkled = (wrinkles, verseApiId, translationId = undefined) => { // @TODO: need this function?
  const wrinkleVariations = wrinkles[verseApiId] || [];
  return wrinkleVariations.some((wrinkle) => {
    if (wrinkle.level_number < globals.srsLevelWrinkleIsEarnedAt) {
      return false;
    } else if (translationId && wrinkle.translation_id !== translationId) {
      return false;
    }

    return true;
  });
};

const isVerseWrinkleInProgress = (wrinkles, verseApiId, translationId = undefined) => { // @TODO: can memoize return of this for same args // @TODO: most of this can be abstracted with isVerseWrinkled // @TODO: need this function?
  const wrinkleVariations = wrinkles[verseApiId] || [];
  return wrinkleVariations.some((wrinkle) => {
    if (wrinkle.level_number >= globals.srsLevelWrinkleIsEarnedAt || wrinkle.level_number === 0) {
      return false;
    } else if (translationId && wrinkle.translation_id !== translationId) {
      return false;
    }

    return true;
  });
};

const isVerseWrinkleUnstarted = (wrinkles, verseApiId, translationId = undefined) => { // @TODO: can memoize return of this for same args // @TODO: most of this can be abstracted with isVerseWrinkled // @TODO: need this function?
  const wrinkleVariations = wrinkles[verseApiId] || [];
  return wrinkleVariations.some((wrinkle) => {
    if (wrinkle.level_number > 0) {
      return false;
    } else if (translationId && wrinkle.translation_id !== translationId) {
      return false;
    }

    return true;
  });
};

const isChapterWrinkled = (wrinkles, book, chapterNum) => { // @TODO: can memoize return of this for same args // @TODO: need this function?
  for (let i = 1; i <= book.chapters_and_verses[chapterNum]; i++) {
    if (!isVerseWrinkled(wrinkles, CPassage.getVerseApiId(book.api_id, chapterNum, i), true)) {
      return false;
    }
  }

  return true;
};

const isBookWrinkled = (wrinkles, book) => { // @TODO: can memoize return of this for same args // @TODO: need this function?
  for (let i = 1; i <= Object.keys(book.chapters_and_verses).length; i++) {
    if (!isChapterWrinkled(wrinkles, book, i)) {
      return false;
    }
  }

  return true;
};

function getReferenceMatches(searchText) {
  function _parseReference(inputString) {
    const regex = /^(\d?\s?[a-zA-Z\s]+)(?:\s+(\d+))?(?:[\s:]+(\d+))?(?:[-](\d+))?$/;
    const match = inputString.trim().match(regex);

    if (!match) {
      return null; // No match, return null
    }

    const bookName = match[1].trim();
    const chapterNumber = match[2] ? parseInt(match[2], 10) : null;
    const startVerseNumber = match[3] ? parseInt(match[3], 10) : null;
    const endVerseNumber = match[4] ? parseInt(match[4], 10) : null;

    return {
      bookName,
      chapterNumber,
      startVerseNumber,
      endVerseNumber,
    };
  }

  const parsedReference = _parseReference(searchText);
  if (!parsedReference) {
    return [];
  }

  return store.getState().books.filter((book) => { // @TODO: make this dynamic...match on book_names.name to get the id, and then match on any other book_name.name
    let bookAlias = [book.name];
    if (book.id === 19) { // Psalm
      bookAlias = ['Psalm', 'Psalms'];
    } else if (book.id === 22) { // Song of Solomon
      bookAlias = ['Song of Songs', 'Songs of Songs', 'Songs of Solomon', 'Song of Solomon'];
    }

    return bookAlias.some((alias) => alias.toLocaleLowerCase().includes(parsedReference.bookName.toLocaleLowerCase()));
  }).map((book) => {
    const numVersesInChapter = book.chapters_and_verses[parsedReference.chapterNumber];
    if (parsedReference.chapterNumber && !numVersesInChapter) {
      // chapter doesn't exist in book
      return null;
    } else if (
      (parsedReference.startVerseNumber && ((parsedReference.startVerseNumber < 1) || (parsedReference.startVerseNumber > numVersesInChapter))) ||
      (parsedReference.endVerseNumber && ((parsedReference.endVerseNumber < 1) || (parsedReference.endVerseNumber > numVersesInChapter))) ||
      (parsedReference.startVerseNumber && parsedReference.endVerseNumber && (parsedReference.startVerseNumber > parsedReference.endVerseNumber))
    ) {
      // given verse(s) don't exist in this chapter, or the order doesn't make sense
      return null;
    }

    return {
      book_id: book.id,
      chapter_number: parsedReference.chapterNumber,
      start_verse_number: parsedReference.startVerseNumber,
      end_verse_number: parsedReference.endVerseNumber,
    };
  }).filter((reference) => reference);
}

function isWrinkleTestOpen(wrinkle) { // @TODO: there should be a wrinkle model/class on the front end, and this should be a class method
  return new Date(wrinkle?.date_next_srs_level).valueOf() <= Date.now();
}

async function updateDisplayName(fbUser, displayName) {
  await fb.updateProfile(fbUser, {
    displayName,
  });
  await fb.getIdToken(fbUser, true); // force jwt refresh so that it contains the user's name
  store.dispatch(fetchUser());
}

function versesToText(verses) { // @TODO: replace this with removeVerseFormatting
  return Object.values(verses).map((v: any) => v.verseText?.trim() || '').filter((t) => t).join(' '); // @TODO: edge cases where there's an m-dash between verses, etc.
}

function semverToLexigraphic(semver) {
  if (!semver) {
    return null;
  }

  const [major, minor, patch] = semver.split('.');

  return `${major}${minor.padStart(3, '0')}${patch.padStart(3, '0')}`;
}

function lexigraphicToSemver(lexigraphic) {
  if (!lexigraphic) {
    return null;
  }

  let lexigraphicString = `${lexigraphic}`;
  const patch = lexigraphicString.slice(-3);
  lexigraphicString = lexigraphicString.slice(0, -3);
  const minor = lexigraphicString.slice(-3);
  lexigraphicString = lexigraphicString.slice(0, -3);
  const major = lexigraphicString || '0';

  return `${+major}.${+minor}.${+patch}`;
}

function normalizeApostrophes(text) {
  return text.replace(/[\u2019\u2018\u02BC\u2032]/g, "'");
}

function _mergeTranslations(existing, incoming) {
  for (const [translationId, incomingData] of Object.entries(incoming) as any) {
    const existingData = existing[translationId] || {
      translation_metadata: [],
      verse_texts: [],
      footnotes: [],
      book_names: [],
    };

    existing[translationId] = {
      translation_metadata: [
        ...existingData.translation_metadata,
        ...incomingData.translation_metadata,
      ],
      verse_texts: [
        ...existingData.verse_texts,
        ...incomingData.verse_texts,
      ],
      footnotes: [
        ...existingData.footnotes,
        ...incomingData.footnotes,
      ],
      book_names: [
        ...existingData.book_names,
        ...incomingData.book_names,
      ],
    };
  }

  return existing;
}

const fetchApiSyncStagingMetadataOutsideOfReduxApiMiddleware = async (successCallback) => { // @TODO: rename
  let endpoint = `${globals.apiUrl}/admin/api_sync_staging_metadata`;
  let allResults: any = {};
  while (true) {
    try {
      const response = await fetch(endpoint, {
        headers: await getRequestHeaders(), // @TODO: is this how we do auth for fetch api?
      });
      const payload = await response.json();
      if (payload.next_url) {
        endpoint = payload.next_url;
      }

      const incoming = payload.api_sync_staging_metadata;
      allResults = {
        ...allResults,

        all_notes: incoming.all_notes, // @NOTE: each page contains all notes, so we can overwrite instead of merging
        pending_batches: incoming.pending_batches, // @NOTE: each page contains all pending_batches, so we can overwrite instead of merging
        translations: _mergeTranslations(
          allResults.translations || {},
          incoming.translations,
        ),
      };

      // @TODO: if (!payload.next_url && !response.error) {
      if (!payload.next_url) { // @TODO: check error status
        store.dispatch(setApiSyncStagingMetadata(allResults));
        successCallback();
        break;
      }
    } catch (error) {
      fb.recordError(fb.crashlytics, error);
      break;
    }
  }
};

export default {
  camelCaseToHumanReadable: commonUtils.camelCaseToHumanReadable,
  snakeToCamel: commonUtils.snakeToCamel,
  isSecondOlderOrSameAsFirst: commonUtils.isSecondOlderOrSameAsFirst,
  linkUserAndCredential: commonUtils.linkUserAndCredential,
  memoAreEqual: commonUtils.memoAreEqual,

  uploadPhoto,

  humanReadableScore,
  humanReadableTime,
  humanReadableTimeExact,
  humanReadableTimeDashboard,
  humanReadableNumber,
  humanReadableBytes,
  prettyCurrency,

  capitalizeFirstLetter,

  createNewPassage,

  removeVerseFormatting,

  getGoalProgress,

  isNewAppVersionWithReleaseNotesAvailable,

  prettyAvailablityDateRange,

  isWrinkleTestOpen,

  saveLesson,

  isVerseWrinkled,
  isVerseWrinkleInProgress,
  isVerseWrinkleUnstarted,
  isChapterWrinkled,
  isBookWrinkled,

  getReferenceMatches,

  retryFailedCompletedLessons,

  updateDisplayName,

  versesToText,

  semverToLexigraphic,
  lexigraphicToSemver,

  normalizeApostrophes,

  fetchApiSyncStagingMetadataOutsideOfReduxApiMiddleware,
};
